import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Calendar, Clock, User, Phone, Mail, Edit, Check, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { mockPatientInfo } from "@/services/mockBookingData";
import { Provider } from "./ProviderCard";
import { useAppointments } from "@/hooks/useAppointments";
import { TriageOutcome, TriageData } from "@/types/triage";

interface BookingDetails {
  provider: Provider;
  date: string;
  formattedDate: string;
  timeSlot: string;
  duration: number;
  triageResults?: TriageOutcome | null;
  triageData?: TriageData | null;
  triageId?: string;
  patientId?: string;
  primarySymptoms?: string;
}

interface PatientInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address?: string;
}

export function AudioTriageBookingConfirm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { addAppointment } = useAppointments();
  
  // Get booking details from location state
  const bookingDetails = location.state as BookingDetails;
  
  // State for patient information
  const [patientInfo, setPatientInfo] = useState<PatientInfo>(mockPatientInfo);
  const [specialRequests, setSpecialRequests] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Handle edit toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  // Handle patient info change
  const handlePatientInfoChange = (field: keyof PatientInfo, value: string) => {
    setPatientInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Generate reason for visit from triage results
  const generateReasonForVisit = () => {
    if (!bookingDetails.triageResults && !bookingDetails.primarySymptoms) {
      return "General consultation";
    }

    // Get urgency from triage results if available
    const urgency = bookingDetails.triageResults?.urgency || "standard";
    
    let reason = "";
    
    // Add urgency level
    switch (urgency) {
      case "immediate":
        reason = "Urgent care needed: ";
        break;
      case "urgent":
        reason = "Prompt attention needed: ";
        break;
      case "non-urgent":
        reason = "Non-urgent consultation: ";
        break;
      default:
        reason = "Consultation for ";
        break;
    }
    
    // Use primary symptoms from booking details if available, otherwise try to get from triage results
    if (bookingDetails.primarySymptoms) {
      reason += bookingDetails.primarySymptoms;
    } else if (bookingDetails.triageResults?.presentingComplaint) {
      reason += bookingDetails.triageResults.presentingComplaint;
    } else if (bookingDetails.triageData?.symptoms && bookingDetails.triageData.symptoms.length > 0) {
      reason += bookingDetails.triageData.symptoms.join(", ");
    } else {
      reason += "symptoms assessment";
    }
    
    return reason;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate required fields
    if (!patientInfo.firstName || !patientInfo.lastName || !patientInfo.email || !patientInfo.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Add the appointment to the shared state
      const newAppointment = addAppointment({
        patient: `${patientInfo.firstName} ${patientInfo.lastName}`,
        provider: bookingDetails.provider.name,
        time: bookingDetails.timeSlot,
        duration: bookingDetails.duration,
        type: "Consultation",
        day: new Date(bookingDetails.date).getDay(),
        date: bookingDetails.date,
        reason: generateReasonForVisit(),
        status: "confirmed",
        specialRequests: specialRequests || undefined,
        // Include triage and patient IDs when available for tracking
        triageId: bookingDetails.triageId,
        patientId: bookingDetails.patientId || patientInfo.firstName.toLowerCase() + patientInfo.lastName.toLowerCase(),
        urgency: bookingDetails.triageResults?.urgency,
      });

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('New appointment added:', newAppointment);
      
      toast({
        title: "Booking Confirmed! 🎉",
        description: `Your appointment with ${bookingDetails.provider.name} has been scheduled for ${bookingDetails.formattedDate} at ${bookingDetails.timeSlot}.`,
      });

      // Navigate to appointments page
      navigate("/patient/appointments");
    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: "Booking Failed",
        description: "There was an error booking your appointment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // If no booking details are available, redirect back to provider select
  if (!bookingDetails || !bookingDetails.provider) {
    navigate("/patient/audio-triage/book/provider-select");
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-8">
      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={handleBack} className="flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <h1 className="text-2xl font-semibold text-center flex-1">Review & Confirm</h1>
        <div className="w-20"></div> {/* Spacer for alignment */}
      </div>

      {/* Appointment Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Appointment Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="font-medium">{bookingDetails.provider.name}</div>
                  <div className="text-sm text-blue-600">{bookingDetails.provider.specialty}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="font-medium">{bookingDetails.formattedDate}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="font-medium">{bookingDetails.timeSlot}</div>
                  <div className="text-xs text-gray-500">{bookingDetails.duration} minutes</div>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <div className="font-medium text-sm text-gray-700">Reason for Visit</div>
                <div className="text-sm text-gray-600">{generateReasonForVisit()}</div>
              </div>
              {bookingDetails.provider.experience && (
                <div>
                  <div className="font-medium text-sm text-gray-700">Provider Experience</div>
                  <div className="text-sm text-gray-600">{bookingDetails.provider.experience}</div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Patient Information */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Your Information</CardTitle>
            <CardDescription>
              Pre-filled from your profile
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleEditToggle}
            className="flex items-center gap-1"
          >
            {isEditing ? (
              <>
                <Check className="w-4 h-4" />
                Done
              </>
            ) : (
              <>
                <Edit className="w-4 h-4" />
                Edit
              </>
            )}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isEditing ? (
                <>
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={patientInfo.firstName}
                      onChange={(e) => handlePatientInfoChange("firstName", e.target.value)}
                      placeholder="Enter your first name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={patientInfo.lastName}
                      onChange={(e) => handlePatientInfoChange("lastName", e.target.value)}
                      placeholder="Enter your last name"
                      required
                    />
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-3 col-span-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <div className="font-medium">{patientInfo.firstName} {patientInfo.lastName}</div>
                </div>
              )}
            </div>

            {isEditing ? (
              <>
                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={patientInfo.email}
                    onChange={(e) => handlePatientInfoChange("email", e.target.value)}
                    placeholder="Enter your email address"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={patientInfo.phone}
                    onChange={(e) => handlePatientInfoChange("phone", e.target.value)}
                    placeholder="Enter your phone number"
                    required
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <div className="font-medium">{patientInfo.email}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <div className="font-medium">{patientInfo.phone}</div>
                </div>
              </>
            )}

            <Separator />

            <div>
              <Label htmlFor="specialRequests">Special Requests (Optional)</Label>
              <Textarea
                id="specialRequests"
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)}
                placeholder="Any special requests or additional information for your provider"
                rows={3}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Important Notes */}
      <Card>
        <CardContent className="p-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Important Notes</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Please arrive 15 minutes before your appointment</li>
              <li>• Bring a valid ID and insurance card</li>
              <li>• You can reschedule up to 24 hours in advance</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Confirm Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSubmit} 
          disabled={isSubmitting}
          className="px-8"
        >
          {isSubmitting ? "Confirming..." : "Confirm Booking"}
        </Button>
      </div>
    </div>
  );
}
